import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Is this a "light" sub-page (portal is light-themed)?
  const isLightPage = ['/book', '/subscribe', '/checkout'].includes(location.pathname);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const [isMobile, setIsMobile] = useState(false);
 
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      // Navigate home first, then scroll
      window.location.href = '/' + id;
      return;
    }
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Background logic
  const navBg = scrolled
    ? isLightPage
      ? 'rgba(250,247,240,0.94)'
      : 'rgba(16,12,6,0.88)'
    : 'transparent';

  const borderColor = scrolled ? 'rgba(200,146,42,0.12)' : 'transparent';
  const textColor = isLightPage ? (scrolled ? '#7A6E5E' : 'rgba(26,18,9,0.7)') : 'rgba(250,247,240,0.75)';
  const logoColor = isLightPage ? '#1A1209' : '#FAF7F0';
  const hamColor  = isLightPage ? '#1A1209' : '#FAF7F0';

  const links = [
    { label: 'Instruments', href: '#instruments' },
    { label: 'AI Portal',   href: '#ai' },
    { label: 'Contact',     href: '#contact' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          padding: '20px 6%', zIndex: 900,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'nowrap',
          background: navBg,
          backdropFilter: scrolled ? 'blur(14px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'none',
          borderBottom: `1px solid ${borderColor}`,
          transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          data-cursor="HOME"
          style={{
            textDecoration: 'none',
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 22, letterSpacing: '4px',
            color: logoColor,
            transition: 'color 0.4s',
          }}
        >
          A<span style={{ color: '#C8922A' }}>A</span>ROHAN
        </Link>

        {/* Desktop Links Container (Only rendered if NOT mobile) */}
        {!isMobile && (
          <ul style={{ display: 'flex', gap: 'clamp(12px, 3vw, 32px)', listStyle: 'none', margin: '0 20px', padding: 0, flex: 1, justifyContent: 'center' }}>
            {links.map(({ label, href }) => (
              <li key={label}>
                <button
                  onClick={() => scrollTo(href)}
                  data-cursor={label.toUpperCase()}
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: 'clamp(9px, 2.5vw, 11px)', fontWeight: 500,
                    letterSpacing: 'clamp(1px, 0.5vw, 2px)', textTransform: 'uppercase',
                    color: textColor,
                    background: 'none', border: 'none', cursor: 'pointer',
                    transition: 'color 0.2s',
                    padding: 0,
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={e => { e.target.style.color = '#C8922A'; }}
                  onMouseLeave={e => { e.target.style.color = textColor; }}
                >{label}</button>
              </li>
            ))}
          </ul>
        )}

        {/* Right actions */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0 }}>
          {/* Book CTA — always visible */}
          <Link
            to="/book"
            data-cursor="BOOK"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: isLightPage ? '#1A1209' : '#FAF7F0',
              border: `1px solid ${isLightPage ? 'rgba(26,18,9,0.3)' : 'rgba(250,247,240,0.25)'}`,
              padding: '8px 18px',
              textDecoration: 'none',
              transition: 'all 0.3s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#C8922A';
              e.currentTarget.style.borderColor = '#C8922A';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = isLightPage ? 'rgba(26,18,9,0.3)' : 'rgba(250,247,240,0.25)';
              e.currentTarget.style.color = isLightPage ? '#1A1209' : '#FAF7F0';
            }}
          >
            Book
          </Link>

          {/* Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 5, padding: 6, flexShrink: 0 }}
            aria-label="Toggle menu"
          >
            {[0, 1, 2].map(i => (
              <motion.span
                key={i}
                animate={
                  mobileOpen
                    ? i === 1 ? { opacity: 0 } : i === 0 ? { rotate: 45, y: 6 } : { rotate: -45, y: -6 }
                    : { opacity: 1, rotate: 0, y: 0 }
                }
                style={{ display: 'block', width: 22, height: 1.5, background: hamColor, borderRadius: 1 }}
              />
            ))}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'fixed', top: 68, left: 0, right: 0, zIndex: 800,
              background: isLightPage ? 'rgba(250,247,240,0.97)' : 'rgba(16,12,6,0.97)',
              backdropFilter: 'blur(12px)',
              borderBottom: '1px solid rgba(200,146,42,0.15)',
              padding: '24px 6%',
              display: 'flex', flexDirection: 'column', gap: 20,
            }}
          >
            {links.map(({ label, href }) => (
              <button
                key={label}
                onClick={() => scrollTo(href)}
                style={{
                  textAlign: 'left', fontSize: 16, fontWeight: 500,
                  color: isLightPage ? '#1A1209' : '#FAF7F0',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: "'DM Sans',sans-serif", padding: 0,
                }}
              >{label}</button>
            ))}
            <Link
              to="/book"
              onClick={() => setMobileOpen(false)}
              style={{
                display: 'inline-block',
                marginTop: 8,
                padding: '14px 28px',
                background: '#C8922A',
                color: '#fff',
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                textDecoration: 'none',
                textAlign: 'center',
              }}
            >
              Book a Session →
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
